import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api, useAuth } from '../contexts/AuthContext';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  User,
  Shield,
  UserCheck,
  Calendar
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Users = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(user?.org_id || '');
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgCompanyName, setNewOrgCompanyName] = useState('');

  const { data: orgs } = useQuery(
    ['orgs'],
    () => api.get('/admin/orgs').then((res) => res.data),
    { enabled: isSuperAdmin }
  );

  React.useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedOrgId) return;
    const first = orgs?.[0]?.id;
    if (first) setSelectedOrgId(first);
  }, [isSuperAdmin, orgs, selectedOrgId]);

  const { data: users, isLoading } = useQuery(
    ['users', search, selectedOrgId],
    () => {
      const params = {};
      if (search) params.search = search;
      if (isSuperAdmin && selectedOrgId) params.org_id = selectedOrgId;
      return api.get('/users', { params }).then(res => res.data);
    },
    { enabled: isAdmin && (!isSuperAdmin || Boolean(selectedOrgId)) }
  );

  const deleteMutation = useMutation(
    (userId) => api.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuário excluído com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir usuário');
      }
    }
  );

  const createOrgMutation = useMutation(
    (payload) => api.post('/admin/orgs', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('orgs');
        const created = res.data;
        setSelectedOrgId(created.id);
        setNewOrgName('');
        setNewOrgCompanyName('');
        toast.success('Empresa criada');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao criar empresa');
      }
    }
  );

  const handleDelete = (userId, userName) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${userName}"?`)) {
      deleteMutation.mutate(userId);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleCreateOrg = (e) => {
    e.preventDefault();
    const name = newOrgName.trim();
    const companyName = newOrgCompanyName.trim();
    if (!name) return toast.error('Informe o nome');
    createOrgMutation.mutate({ name, company_name: companyName || name });
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Restrito</h3>
        <p className="text-gray-500">Apenas administradores podem acessar esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600">Gerencie os usuários do sistema</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Usuário
        </button>
      </div>

      {isSuperAdmin && (
        <div className="card">
          <div className="card-body space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <select
                  className="input"
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                >
                  <option value="" disabled>Selecione</option>
                  {(orgs || []).map((o) => (
                    <option key={o.id} value={o.id}>{o.company_name || o.name}</option>
                  ))}
                </select>
              </div>
              <form onSubmit={handleCreateOrg} className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nova empresa</label>
                    <input
                      className="input"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      placeholder="Nome interno"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome exibido (opcional)</label>
                    <input
                      className="input"
                      value={newOrgCompanyName}
                      onChange={(e) => setNewOrgCompanyName(e.target.value)}
                      placeholder="Nome no relatório"
                    />
                  </div>
                </div>
                <button className="btn-secondary" type="submit" disabled={createOrgMutation.isLoading}>
                  Criar empresa
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="p-6">
              <LoadingSpinner />
            </div>
          ) : users?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Usuário</th>
                    <th className="table-header-cell">Email</th>
                    <th className="table-header-cell">Função</th>
                    <th className="table-header-cell">Criado em</th>
                    <th className="table-header-cell">Último Login</th>
                    <th className="table-header-cell">Ações</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {users.map((userItem) => (
                    <tr key={userItem.id}>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {userItem.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{userItem.email}</div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          userItem.role === 'superadmin'
                            ? 'badge-danger'
                            : userItem.role === 'admin'
                              ? 'badge-danger'
                              : 'badge-primary'
                        }`}>
                          {userItem.role === 'superadmin'
                            ? 'Super Admin'
                            : userItem.role === 'admin'
                              ? 'Administrador'
                              : 'Usuário'}
                        </span>
                        {userItem.must_change_password && (
                          <div className="text-xs text-amber-700 mt-1">Troca de senha pendente</div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(userItem.created_at)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-500">
                          {userItem.last_login ? formatDate(userItem.last_login) : 'Nunca'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              // TODO: Implement edit user
                              toast.info('Funcionalidade em desenvolvimento');
                            }}
                            className="text-primary-600 hover:text-primary-500"
                            title="Editar usuário"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {userItem.id !== user.id && (
                            <button
                              onClick={() => handleDelete(userItem.id, userItem.name)}
                              className="text-danger-600 hover:text-danger-500"
                              title="Excluir usuário"
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center">
              <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-2">Nenhum usuário encontrado</h3>
              <p className="text-sm text-gray-500 mb-4">
                {search ? 'Tente ajustar os termos de busca' : 'Comece criando o primeiro usuário'}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Usuário
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          isSuperAdmin={isSuperAdmin}
          orgs={orgs || []}
          selectedOrgId={selectedOrgId}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries('users');
          }}
        />
      )}
    </div>
  );
};

const CreateUserModal = ({ onClose, onSuccess, isSuperAdmin, orgs, selectedOrgId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'operator',
    org_id: selectedOrgId || ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role
      };
      if (isSuperAdmin) payload.org_id = formData.org_id || selectedOrgId;
      await api.post('/users', payload);
      toast.success('Usuário criado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Criar Novo Usuário</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apelido
              </label>
              <input
                type="text"
                name="name"
                required
                className="input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nome completo do usuário"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                className="input"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <input
                type="password"
                name="password"
                required
                minLength={8}
                className="input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 8 caracteres"
              />
            </div>

            {isSuperAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <select
                  name="org_id"
                  className="input"
                  value={formData.org_id}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Selecione</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.company_name || o.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Função
              </label>
              <select
                name="role"
                className="input"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="operator">Usuário</option>
                <option value="admin">Administrador</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Criar Usuário
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Users;
